import { MenuItem, Tooltip } from '@chakra-ui/react';
import { t } from '@transifex/native';
import { FC, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { app, projects } from '@xmcloud/core/messages/en';
import {
	CircularLoading,
	GenericErrorTemplate,
	HeaderStack,
	XMCSBreadCrumb,
	XMCSMenuButton,
	XMCSTabs,
} from 'app/shared-components';
import { goTo, projectPaths } from '@xmcloud/core/routes/paths';
import { useGetProject } from 'app/services/projects';
import DeleteProject from 'app/pages/project/components/DeleteProject';
import ConvertProjectModal from 'app/pages/project/components/ConvertProjectModal';
import { projectPageItems } from 'app/shared-components/breadcrumb/items';
import {
	projectTabItems,
	projectTabItemsLegacy,
} from 'app/shared-components/tab/items';
import { handlePush } from '@xmcloud/utils/helpers';
import { getErrorConfig } from '@xmcloud/utils/errorConfig';
import { useCreateProjectV3 } from 'app/pages/create-project/old-components/useCreateProjectV3';
import { useProjectConversion } from 'app/hooks/useProjectConversion';
import { useFeature } from 'app/feature-flag/features';

const {
	canNotBeDeleted,
	edit,
	delete: enDelete,
	convert,
	convertUnavailable,
} = projects;
const {
	projectDetails: projectDetailsPath,
	ehEnvironments: ehEnvironmentsPath,
	cmEnvironments: cmEnvironmentsPath,
	environments: environmentsPathLegacy,
} = projectPaths;

const { options: optionstxt } = app;

export function getTabIndex(isNewEnvsRoute: boolean, pathname: string) {
	if (isNewEnvsRoute) {
		if (pathname.includes(cmEnvironmentsPath)) return 0;
		if (pathname.includes(ehEnvironmentsPath)) return 1;
		if (pathname.includes(projectDetailsPath)) return 2;
	} else {
		if (pathname.includes(projectDetailsPath)) return 1;
		if (pathname.includes(environmentsPathLegacy)) return 0;
	}
	return 0;
}

export const ProjectPage: FC = () => {
	const { projectId } = useParams<{ projectId: string }>();
	const { pathname } = useLocation();
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [openConvertModal, setOpenConvertModal] = useState(false);
	const { data, isLoading, error } = useGetProject({
		id: projectId as string,
	});
	const { enableCreateProjectV3 } = useCreateProjectV3();
	const isConvertProjectEnabled = useFeature('ConvertProject');

	// Use the custom conversion hook
	const { handleConvert, shouldShowConvertOption, isConverting } =
		useProjectConversion({
			projectId: projectId as string,
			onModalClose: () => setOpenConvertModal(false),
		});

	const isNewEnvsRoute =
		!!data?.data?.environments[0]?.type ||
		(enableCreateProjectV3 && !data?.data?.environments.length);

	const isLegacyProject =
		!data?.data?.environments[0]?.type &&
		!(enableCreateProjectV3 && !data?.data?.environments.length);

	const tabItems = isNewEnvsRoute ? projectTabItems : projectTabItemsLegacy;

	const tabIndex = getTabIndex(isNewEnvsRoute, pathname);

	const projectName = data?.data.name;

	const environmentsLength = data?.data?.environments?.length;

	const showConvertOption = shouldShowConvertOption(data?.data);

	if (isLoading) return <CircularLoading />;

	if (error) {
		const { variant, text } = getErrorConfig(error, 'project');
		return <GenericErrorTemplate variant={variant} text={text} />;
	}

	return (
		<>
			<XMCSBreadCrumb items={projectPageItems} />
			<HeaderStack
				title={projectName}
				mb={2}
				tagText="Legacy"
				tagColorScheme="neutral"
				hasTag={isLegacyProject}
			>
				<nav
					aria-label={t('Actions for {projectName}', {
						projectName,
					})}
				>
					<XMCSMenuButton
						text={optionstxt}
						{...{
							disabled: !data?.data,
							'data-testid': 'menu-button',
						}}
					>
						<MenuItem
							onClick={() =>
								handlePush(goTo.editProject(projectId!))
							}
							data-testid="menu-edit-item"
						>
							{t(edit)}
						</MenuItem>

						{showConvertOption && (
							<MenuItem
								onClick={() => setOpenConvertModal(true)}
								data-testid="menu-convert-item"
								isDisabled={!isConvertProjectEnabled}
							>
								<Tooltip
									label={t(convertUnavailable)}
									isDisabled={isConvertProjectEnabled}
									shouldWrapChildren
								>
									{t(convert)}
								</Tooltip>
							</MenuItem>
						)}

						<MenuItem
							onClick={() => setOpenDeleteModal(true)}
							data-testid="menu-delete-item"
							isDisabled={Boolean(environmentsLength)}
						>
							<Tooltip
								label={t(canNotBeDeleted)}
								isDisabled={!environmentsLength}
								shouldWrapChildren
							>
								{t(enDelete)}
							</Tooltip>
						</MenuItem>
					</XMCSMenuButton>
				</nav>
			</HeaderStack>
			<XMCSTabs
				tabIndex={tabIndex}
				tabItems={tabItems}
				state={{ projectName }}
				args={[projectId as string]}
			/>
			{openDeleteModal && (
				<DeleteProject
					openModal={openDeleteModal}
					setOpenModal={setOpenDeleteModal}
					projectDetails={{
						id: projectId as string,
						name: projectName!,
					}}
				/>
			)}
			{openConvertModal && (
				<ConvertProjectModal
					openModal={openConvertModal}
					setOpenModal={setOpenConvertModal}
					onConfirm={handleConvert}
					isLoading={isConverting}
				/>
			)}
			<Outlet />
		</>
	);
};
